import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, X, Plus } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import PullToRefresh from '../../components/PullToRefresh'
import { useExercises } from '../../hooks/useExercises'
import { useWorkoutTemplates } from '../../hooks/useWorkoutTemplates'
import { performRefresh } from '../../lib/refresh'
import WorkoutBackHeader from './components/WorkoutBackHeader'
import ExercisePicker from './components/ExercisePicker'

const DAYS = ['day1_chest_back', 'day2_arms_shoulders', 'day3_legs']

function move(arr, from, to) {
  if (to < 0 || to >= arr.length) return arr
  const next = [...arr]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

export default function TemplatesEditor() {
  const { t } = useTranslation()
  const { exercises, loading: exLoading, refetch: refetchExercises } = useExercises()
  const { templates, setTemplate } = useWorkoutTemplates()
  // Templates live on the profile row and are refetched whenever the
  // profile is — useWorkoutTemplates itself doesn't expose a refetch.
  // Pulling here refreshes the exercise library, which is the visible
  // data on this page; templates auto-sync via the profile hook.
  const handleRefresh = useCallback(() => performRefresh([refetchExercises]), [refetchExercises])
  const [pickerDay, setPickerDay] = useState(null)
  const [savingDay, setSavingDay] = useState(null)

  const exById = useMemo(() => {
    const m = new Map()
    for (const e of exercises) m.set(e.id, e)
    return m
  }, [exercises])

  const persist = async (day, ids) => {
    setSavingDay(day)
    await setTemplate(day, ids)
    setSavingDay(null)
  }

  const handleMove = (day, from, to) => {
    const ids = templates[day] || []
    persist(day, move(ids, from, to))
  }

  const handleRemove = (day, idx) => {
    const ids = (templates[day] || []).filter((_, i) => i !== idx)
    persist(day, ids)
  }

  const handleAdd = (exerciseId) => {
    const day = pickerDay
    if (!day) return
    const ids = templates[day] || []
    if (ids.includes(exerciseId)) {
      setPickerDay(null)
      return
    }
    persist(day, [...ids, exerciseId])
    setPickerDay(null)
  }

  if (exLoading) {
    return (
      <div className="space-y-4 content-fade">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-4 content-fade">
      <WorkoutBackHeader
        title={t('workout.templates.title')}
        subtitle={t('workout.templates.subtitle')}
        fallback="/workout"
      />

      {DAYS.map((day) => {
        const ids = templates[day] || []
        const rows = ids.map((id) => ({ id, exercise: exById.get(id) }))
        return (
          <Card key={day}>
            <CardContent className="p-3 sm:p-4 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">
                  {t(`workout.templates.${day}`)}
                </p>
                {savingDay === day && (
                  <span className="text-xs text-muted-foreground">{t('workout.templates.saving')}</span>
                )}
              </div>

              {rows.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">
                  {t('workout.templates.empty')}
                </p>
              ) : (
                <ul className="space-y-1">
                  {rows.map((row, idx) => (
                    <li key={row.id} className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                      <span className="text-xs text-muted-foreground w-5 text-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {row.exercise?.name || t('workout.session.missingExercise')}
                        </p>
                        {row.exercise && (
                          <p className="text-[11px] text-muted-foreground truncate">
                            {t(`workout.muscleGroup.${row.exercise.muscle_group}`)} · {t(`workout.equipment.${row.exercise.equipment_type}`)}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleMove(day, idx, idx - 1)}
                        disabled={idx === 0 || savingDay === day}
                        className="h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground disabled:opacity-30"
                        aria-label={t('workout.templates.moveUp')}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMove(day, idx, idx + 1)}
                        disabled={idx === rows.length - 1 || savingDay === day}
                        className="h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground disabled:opacity-30"
                        aria-label={t('workout.templates.moveDown')}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(day, idx)}
                        disabled={savingDay === day}
                        className="h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 disabled:opacity-30"
                        aria-label={t('workout.templates.remove')}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPickerDay(day)}
                disabled={savingDay === day}
                className="w-full"
              >
                <Plus className="w-4 h-4 me-1.5" />
                {t('workout.templates.add')}
              </Button>
            </CardContent>
          </Card>
        )
      })}

      {pickerDay && (
        <ExercisePicker
          exercises={exercises}
          excludeIds={templates[pickerDay] || []}
          onPick={handleAdd}
          onClose={() => setPickerDay(null)}
        />
      )}
    </div>
    </PullToRefresh>
  )
}
